import { ReferralStatus } from '@prisma/client';
import {
  CheckCircle,
  Copy,
  Loader,
  Loader2,
  Mail,
  MailOpen,
  Send,
  SquareCheck,
  User,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import { GiftsBackgroundSvg } from '@/code/frontend/assets/gifts';
import { Pagination } from '@/code/frontend/components/table/pagination';
import { Button } from '@/code/frontend/components/ui/button';
import { toast } from '@/code/frontend/hooks/use-toast';
import { LinkRouter } from '@/code/lib/routes';
import { cn } from '@/code/lib/utils/cn';
import {
  getReferralStatusDisplay,
  getReferralUrl,
} from '@/code/lib/utils/referral';
import { trpc } from '@/code/lib/utils/trpc';

type ReferralPageProps = {
  closeDialog: () => void;
};

const ITEMS_PER_PAGE = 5;

export const ReferralPage: React.FC<ReferralPageProps> = ({ closeDialog }) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [successInviteSent, setSuccessInviteSent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    linkedinUrl: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    linkedinUrl: '',
  });
  const [copiedReferralId, setCopiedReferralId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // tRPC queries
  const generalReferralQuery = trpc.referrals.getGeneralReferralLink.useQuery();
  const referralsQuery = trpc.referrals.getReferrals.useQuery();
  const createSpecificReferralMutation =
    trpc.referrals.createSpecificReferral.useMutation();
  const utils = trpc.useUtils();

  // No need for local status mapping - use shared utility

  const handleCopyLink = async () => {
    if (generalReferralQuery.data?.referralLink) {
      await navigator.clipboard.writeText(
        generalReferralQuery.data.referralLink,
      );
      toast({
        title: 'Success',
        description: 'Referral link copied to clipboard!',
      });
      setCopied(true);
    }
  };

  const handleCopySpecificReferralLink = async (
    referralCode: string,
    referralId: string,
  ) => {
    // Use shared utility function
    const referralLink = getReferralUrl(referralCode);

    await navigator.clipboard.writeText(referralLink);
    setCopiedReferralId(referralId);
    toast({
      title: 'Success',
      description: 'Referral link copied to clipboard!',
    });
  };

  const handleInviteEmail = () => {
    setShowInviteForm(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;

    try {
      await createSpecificReferralMutation.mutateAsync({
        refereeName: formData.name,
        refereeEmail: formData.email,
        linkedinUrl: formData.linkedinUrl,
      });

      toast({
        title: 'Success',
        description: `Referral invitation sent to ${formData.name}!`,
      });
      setFormData({ name: '', email: '', linkedinUrl: '' });
      setFormErrors({ name: '', email: '', linkedinUrl: '' });
      setSuccessInviteSent(true);

      // Refresh referrals list
      await utils.referrals.getReferrals.invalidate();
    } catch (e) {
      if (e instanceof Error && e.message.includes('already been referred')) {
        toast({
          title: 'Error',
          description: 'This person has already been referred',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send referral invitation',
          variant: 'destructive',
        });
      }
    }
  };

  // Validate LinkedIn URL
  const isValidLinkedInUrl = (url: string): boolean => {
    if (!url) return false;
    const linkedInRegex =
      /^(https?:\/\/)?(www\.)?(linkedin\.com|linked\.in)\/(in|pub|profile)\/[a-zA-Z0-9-._~]+\/?$/i;
    return linkedInRegex.test(url);
  };

  // Validate email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    return (
      formData.name.trim().length > 0 &&
      isValidEmail(formData.email) &&
      isValidLinkedInUrl(formData.linkedinUrl)
    );
  };

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 5000);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!successInviteSent) return;
    const timer = setTimeout(() => {
      setSuccessInviteSent(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [successInviteSent]);

  useEffect(() => {
    if (copiedReferralId == null) return;
    const timer = setTimeout(() => setCopiedReferralId(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedReferralId]);

  // Render status icon based on shared mapping in referral utils
  const renderStatusIcon = (status: ReferralStatus) => {
    const iconKey = getReferralStatusDisplay(status).icon;
    switch (iconKey) {
      case 'mail':
        return <Mail className="size-4 mr-2" />;
      case 'mail-open':
        return <MailOpen className="size-4 mr-2" />;
      case 'user-plus':
        return <UserPlus className="size-4 mr-2" />;
      case 'user-check':
        return <UserCheck className="size-4 mr-2" />;
      case 'loader':
        return <Loader className="size-4 mr-2 animate-spin" />;
      case 'square-check':
        return <SquareCheck className="size-4 mr-2" />;
      default:
        return null;
    }
  };

  // Compute status chip classes
  const getStatusChipClass = (
    status: ReferralStatus,
    isCopied: boolean,
  ): string =>
    isCopied
      ? 'bg-system-secondary-container text-system-secondary-on-container'
      : status === ReferralStatus.INVITE_SENT
        ? 'bg-system-surface-low text-system-text-subheading hover:bg-system-surface-low/90'
        : status === ReferralStatus.LINK_CLICKED
          ? 'bg-system-surface-low text-system-text-subheading hover:bg-system-surface-low/90'
          : status === ReferralStatus.APP_VISITED
            ? 'bg-system-primary-container text-system-primary-on-primary-container hover:bg-system-primary-container/90'
            : status === ReferralStatus.ACCOUNT_CREATED
              ? 'bg-system-secondary-container text-system-secondary-on-container hover:bg-system-secondary-container/90'
              : status === ReferralStatus.REFEREE_PAID
                ? 'bg-system-secondary-container text-system-secondary-on-container hover:bg-system-secondary-container/90'
                : status === ReferralStatus.REWARDS_SENT
                  ? 'bg-system-secondary-container text-system-secondary-on-container hover:bg-system-secondary-container/90'
                  : 'bg-system-surface-low text-system-text-subheading hover:bg-system-surface-low/90';

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    setFormErrors((prev) => ({ ...prev, [field]: '' }));

    // Validate on change
    if (field === 'email' && value && !isValidEmail(value)) {
      setFormErrors((prev) => ({
        ...prev,
        email: 'Please enter a valid email',
      }));
    }

    if (field === 'linkedinUrl' && value && !isValidLinkedInUrl(value)) {
      setFormErrors((prev) => ({
        ...prev,
        linkedinUrl:
          'Please enter a valid LinkedIn profile URL (e.g., linkedin.com/in/username)',
      }));
    }
  };

  // Memoized computation for referral data processing
  const { paginatedItems, totalItems } = useMemo(() => {
    if (!referralsQuery.data || referralsQuery.data.length === 0) {
      return { paginatedItems: [], totalItems: 0 };
    }

    // Get general referral
    const generalReferral = referralsQuery.data.find(
      (r) => r.isGeneralReferral,
    );

    // Get specific referrals
    const specificReferrals = referralsQuery.data.filter(
      (r) => !r.isGeneralReferral,
    );

    // Extract users from general referral
    const generalReferralUsers: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      primaryEmail: string | null;
      createdAt: Date;
      isFromGeneralLink: true;
    }> = [];

    if (generalReferral) {
      const userMap = new Map<string, (typeof generalReferralUsers)[0]>();
      for (const event of generalReferral.referralEvents) {
        if (event.eventType === 'ACCOUNT_CREATED' && event.referredUser) {
          const user = event.referredUser;
          if (!userMap.has(user.id)) {
            userMap.set(user.id, {
              ...user,
              createdAt: event.createdAt,
              isFromGeneralLink: true,
            });
          }
        }
      }
      generalReferralUsers.push(...Array.from(userMap.values()));
    }

    // Combine all items for display
    const allItems = [
      ...generalReferralUsers.map((user) => ({
        type: 'generalUser' as const,
        data: user,
      })),
      ...specificReferrals.map((referral) => ({
        type: 'specificReferral' as const,
        data: referral,
      })),
    ];

    // Paginate allItems
    const paginatedItems = allItems.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE,
    );

    return { paginatedItems, totalItems: allItems.length };
  }, [referralsQuery.data, currentPage]);

  return (
    <div className="flex flex-col h-[80vh]">
      {/* Fixed Header */}
      <div className="flex items-center justify-between border-b px-6 py-5 border-system-outline">
        <h1 className="text-[1.25rem] leading-[1.5rem] font-medium text-system-text-heading">
          Refer&Earn
        </h1>
        <Button
          onClick={closeDialog}
          iconLeft={<X />}
          variant="airfoil-secondary"
          size="airfoil-icon"
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-[6.75rem] py-4 space-y-10">
        {/* Welcome Section */}
        <div className="flex flex-col items-center text-center py-5">
          <h2 className="font-serif text-[32px] leading-tight text-system-text-heading mb-1">
            Grow your network, grow your rewards
          </h2>
          <p className="text-system-text-subheading mx-auto">
            Introduce Aurium to your network and watch the value multiply
          </p>
        </div>

        {/* Give $250, get $250 Card */}
        <div className="relative rounded-3xl border border-system-outline p-2 shadow">
          {/* Background Decorations */}
          <div className="select-none absolute overflow-hidden inset-0 m-2 border border-system-outline rounded-2xl bg-dotted-sm">
            <div className="absolute left-0 right-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent to-system-outline/20"></div>
            <GiftsBackgroundSvg className="absolute bottom-0 right-4" />
          </div>
          <div className="absolute rounded-3xl select-none inset-0 bg-gradient-to-b from-white to-transparent"></div>

          <div className="flex flex-col relative h-full min-h-[386px] z-10">
            <div className="space-y-6 px-10 pt-11 flex-shrink-0">
              <h3 className="text-2xl font-serif font-medium text-system-text-heading">
                Give $250, get $250!
              </h3>
              <p className="max-w-[568px] text-system-text-subheading text-sm">
                Refer business leaders or sales professionals to our platform.
                Once they subscribe to a paid plan, you will both receive a $250
                reward!
              </p>

              {!showInviteForm ? (
                <div className="flex gap-6">
                  <Button
                    onClick={handleInviteEmail}
                    variant="airfoil-primary"
                    iconLeft={<Mail />}
                  >
                    Invite via email
                  </Button>
                  <Button
                    onClick={handleCopyLink}
                    variant="airfoil-secondary"
                    disabled={!generalReferralQuery.data}
                    iconLeft={
                      copied ? (
                        <CheckCircle />
                      ) : generalReferralQuery.isLoading ? (
                        <Loader2 className=" animate-spin" />
                      ) : (
                        <Copy />
                      )
                    }
                  >
                    {copied ? 'Copied!' : 'Copy referral link'}
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleFormSubmit}
                  className="space-y-6 max-w-[534px]"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                    <div className="space-y-1.5 col-span-2">
                      <span className="text-system-text-heading font-medium text-sm">
                        Recipient's first name
                      </span>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange('name', e.target.value)
                        }
                        className={cn(
                          'text-sm w-full px-3 py-2.5 border rounded-[.625rem] focus:ring-system-text-subheading border-system-outline shadow placeholder:text-system-text-supporting',
                          formErrors.name && 'border-red-300',
                        )}
                        placeholder="First name"
                      />
                      {formErrors.name && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.name}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5 col-span-3">
                      <span className="text-system-text-heading font-medium text-sm">
                        Email
                      </span>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange('email', e.target.value)
                        }
                        className={cn(
                          'text-sm w-full px-3 py-2.5 border rounded-[.625rem] focus:ring-system-text-subheading border-system-outline shadow placeholder:text-system-text-supporting',
                          formErrors.email && 'border-red-300',
                        )}
                        placeholder="hello@email.com"
                      />
                      {formErrors.email && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5 col-span-5">
                      <span className="text-system-text-heading font-medium text-sm">
                        LinkedIn URL
                      </span>
                      <input
                        type="url"
                        required
                        value={formData.linkedinUrl}
                        onChange={(e) =>
                          handleInputChange('linkedinUrl', e.target.value)
                        }
                        className={cn(
                          'text-sm w-full px-3 py-2.5 border rounded-[.625rem] focus:ring-system-text-subheading border-system-outline shadow placeholder:text-system-text-supporting',
                          formErrors.linkedinUrl && 'border-red-300',
                        )}
                        placeholder="https://www.linkedin.com/in/username"
                      />
                      {formErrors.linkedinUrl && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.linkedinUrl}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {successInviteSent ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-system-success" />
                        <span className="text-sm text-system-success">
                          Invite sent!
                        </span>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          variant="airfoil-primary"
                          size="sm"
                          disabled={
                            !isFormValid() ||
                            createSpecificReferralMutation.isPending
                          }
                          iconRight={<Send />}
                          loading={createSpecificReferralMutation.isPending}
                        >
                          Send email
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="airfoil-secondary"
                          disabled={createSpecificReferralMutation.isPending}
                          onClick={() => {
                            setShowInviteForm(false);
                            setFormData({
                              name: '',
                              email: '',
                              linkedinUrl: '',
                            });
                            setFormErrors({
                              name: '',
                              email: '',
                              linkedinUrl: '',
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>
            <div className="flex-1" />
            <div className="px-10 pb-3 pt-8 text-system-text-supporting flex-shrink-0">
              Please note{' '}
              <Link
                className="underline underline-offset-4"
                href={LinkRouter.routeForTerms()}
                target="_blank"
              >
                terms and conditions
              </Link>{' '}
              may apply.
            </div>
          </div>
        </div>

        {/* Your Referrals Section */}
        <div className="space-y-5 py-5">
          <h3 className="text-2xl font-serif text-system-text-heading">
            Your referrals
          </h3>

          <div className="rounded-3xl shadow border border-system-outline p-1">
            {referralsQuery.isLoading ? (
              <div className="h-[232px] flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : referralsQuery.data && referralsQuery.data.length === 0 ? (
              <div className="h-[232px] flex items-center justify-center py-16 flex-col gap-3 text-sm text-system-text-supporting">
                <div className="bg-system-surface-container size-8 rounded-[0.625rem] grid place-content-center">
                  <Mail className="size-5 text-system-text-paragraph" />
                </div>

                <div className="space-y-2 text-center">
                  <h3 className="font-serif text-xl text-system-text-heading">
                    No invited referrals
                  </h3>
                  <p className="text-sm text-system-text-subheading">
                    Start inviting people to the platform and earn rewards
                  </p>
                </div>
              </div>
            ) : referralsQuery.data && paginatedItems.length > 0 ? (
              <>
                {/* Table Header */}
                <div className="grid grid-cols-3 rounded-full gap-4 bg-system-surface-lowest font-medium text-sm text-system-text-subheading mb-1">
                  <div className="px-4 py-3.5">Referral</div>
                  <div className="px-4 py-3.5">Status</div>
                  <div className="px-4 py-3.5">Description</div>
                </div>

                {/* Table Rows */}
                <>
                  {paginatedItems.map((item) => {
                    if (item.type === 'generalUser') {
                      const user = item.data;
                      return (
                        <div
                          key={user.id}
                          className="relative after:rounded-[1.25rem] after:absolute after:inset-0 after:hover:shadow group rounded-[1.25rem] border hover:border-system-outline border-transparent grid grid-cols-3 gap-4 items-center"
                        >
                          <div className="flex h-full items-center px-3.5 py-4 ">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <div className="text-system-text-subheading font-medium">
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : 'Via general referral link'}
                              </div>
                              <div className="text-sm text-system-text-supporting">
                                {user.primaryEmail}
                              </div>
                            </div>
                          </div>

                          <div className="flex h-full items-center px-3.5 py-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-green-100 text-green-700">
                              Account created
                            </span>
                          </div>

                          <div className="font-medium text-system-text-subheading h-full px-3.5 py-4">
                            Signed up through your general referral link
                          </div>
                        </div>
                      );
                    }
                    const referral = item.data;
                    return (
                      <div
                        key={referral.id}
                        className="grid grid-cols-3 gap-4 items-center relative after:rounded-[1.25rem] after:absolute after:inset-0 after:hover:shadow group rounded-[1.25rem] border hover:border-system-outline border-transparent"
                      >
                        <div className="flex h-full items-center px-3.5 py-4">
                          <div className="size-8 bg-system-tertiary rounded-full flex items-center justify-center mr-3">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-system-text-subheading text-sm font-medium">
                              {referral.refereeName || 'Name pending'}
                            </div>
                            <div className="text-xs text-system-text-supporting">
                              {referral.refereeEmail || 'Invitation sent'}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center h-full px-3.5 py-4">
                          <button
                            onClick={() =>
                              handleCopySpecificReferralLink(
                                referral.referralCode,
                                referral.id,
                              )
                            }
                            className={cn(
                              'h-fit inline-flex items-center px-1.5 py-1 rounded-lg text-xs transition-all cursor-pointer',
                              getStatusChipClass(
                                referral.status,
                                copiedReferralId === referral.id,
                              ),
                            )}
                          >
                            {copiedReferralId === referral.id ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Copied!
                              </>
                            ) : (
                              <>
                                {renderStatusIcon(referral.status)}
                                {
                                  getReferralStatusDisplay(referral.status)
                                    .label
                                }
                              </>
                            )}
                          </button>
                        </div>

                        <div className="justify-center px-3.5 py-4 h-full flex flex-col">
                          {getReferralStatusDisplay(
                            referral.status,
                            new Date(referral.createdAt),
                          )
                            .description(new Date(referral.createdAt))
                            .split(' - ')
                            .map((part, i) => (
                              <span
                                key={part}
                                className={cn(
                                  'text-system-text-paragraph font-medium text-sm',
                                  i === 1 &&
                                    'font-normal text-xs text-system-text-subheading',
                                )}
                              >
                                {part}
                              </span>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                </>
              </>
            ) : (
              <div className="flex items-center gap-4 py-6 px-3">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    Your referrals will be shown here
                  </p>
                  <p className="text-xs text-gray-400">
                    Make your first referral today!
                  </p>
                </div>
                <div className="text-sm text-gray-400">
                  waiting on the first referral
                </div>
              </div>
            )}
          </div>
        </div>

        {/* How Referrals Work Section */}
        <div className="space-y-5 mt-5">
          <h3 className="text-2xl font-serif text-system-text-heading">
            How referrals work
          </h3>

          <div className="space-y-7">
            <div className="space-y-2">
              <h4 className="leading-relaxed text-system-text-paragraph mb-2">
                What counts as a successful referral?
              </h4>
              <p className="text-sm text-system-text-subheading">
                A successful Aurium referral qualifies for a bonus when the
                referred business signs up for Aurium and creates their first
                outreach campaign. Once their campaign is launched, your
                referral will be automatically completed and respective bonuses
                will be processed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
